
import React, {FC, useEffect, useMemo, useRef} from 'react';
import classNames from 'classnames';
import {Location} from 'components/TimePicker/types';
import {getDistance} from 'geolib';
import {LngLatBounds} from 'mapbox-gl';
import {Assistant} from 'pages/booking/components/Assistant';
import {useContextLocation} from 'pages/booking/components/useLocation';
import SimpleBar from 'simplebar';
import {sortkey} from 'utils/sort';

import {EventLocation} from './EventLocation';

import * as styles from './styles.module.scss';

type EventsProps = {
	events: {
		loading: boolean
		locations: Location[]
	}
	bounds?: LngLatBounds
	show?: boolean
}

export const EventsList: FC<EventsProps> = ({events, bounds, show}) => {
	const {configs} = useContextLocation();
	const locs = useRef<HTMLDivElement>(null);
	const sbar = useRef<SimpleBar | null>(null);

	// Initialize SimpleBar only once
	useEffect(() => {
		if (locs.current && !sbar.current) {
			sbar.current = new SimpleBar(locs.current, {
				autoHide: false,
				forceVisible: true,
				classNames: {
					vertical: classNames(
						'simplebar-vertical',
						styles.bar,
					),
				}
			});
		}
	}, [locs.current]);

	// Recalculate SimpleBar when locations change
	useEffect(() => {
		if (sbar.current) {
			sbar.current.recalculate();
		}
	}, [sbar.current, events.locations]);

	// Sort our locations according to configs
	const locations = useMemo(() => {
		const target = bounds?.getCenter() || configs.coords;
		const base = events.locations;
		const filter = (loc: Location) => {
			if (bounds && !bounds.contains(loc.details.coords)) {
				return false;
			} else if (loc.datesLeft.length === 0) {
				return false;
			}
			return true;
		};

		// Check if we can/should sort by distance to target
		const locs = base.filter(filter);
		if (!target) {
			return locs;
		}

		// Apply distance sort
		return locs.sort(
			sortkey((loc) => getDistance(target, loc.details.coords))
		);
	}, [
		configs.coords,
		events.locations,
		bounds,
	]);

	return (
		<div className={classNames({
			[styles.list]: true,
			[styles.show]: show,
		})}>
			<Assistant />
			<div className={styles.locations}>
				<div className={styles.scroller} ref={locs}>
					<div className={styles.contents}>
						{locations.map((location, i) => (
							<>
								<hr />
								<EventLocation key={i} location={location} />
							</>
						))}
						<hr />
					</div>
				</div>
			</div>
		</div>
	);
};
